/**
 * Header menu — sub-menus on every breakpoint, plus the mobile panel.
 *
 * A parent item is a `.menu-item__toggle` button (ESR_Menu_Walker with
 * `submenu_toggles`); clicking it toggles `is-open` on its <li> (one open at a
 * time) and `aria-expanded`.
 *
 * Above md: an open parent drops its sub-menu under the bar on the shared band
 * (`.header.is-submenu-open`, `--header-dropdown-height` = the tallest
 * sub-menu, measured here). While one is open, pointing at another parent
 * switches to it and pointing at an item without pages closes it, as do a
 * click outside the header, Escape, a page change, or the pointer staying off
 * the header (bar + band) for `LEAVE_CLOSE_DELAY` ms.
 *
 * md and below: the hamburger (`.header__menu-toggle`) toggles
 * `body.mobile-menu-open` (the panel's open state is CSS) and stops / restarts
 * Lenis; parents open their sub-menu in place as an accordion. Same-page anchor
 * links and Escape close the panel; Barba page changes and resizing across md
 * tear everything down without animation.
 */

import { getLenisInstance } from '../../core/scroll-manager';
import { isMaxBreakpoint } from '../../core/utils/breakpoints.js';

/** How long the pointer may stay off the header before an open drop-down closes. */
const LEAVE_CLOSE_DELAY = 2500;

export function initializeMobileMenu() {
    const header = document.getElementById('header');
    if (!header) return;

    const panel = header.querySelector('.header__menu-panel');
    if (!panel) return;

    const toggle = header.querySelector('.header__menu-toggle');
    const parents = [...panel.querySelectorAll('.menu-item__toggle')];
    const isMobile = () => isMaxBreakpoint('md');
    const body = document.body;

    // -------------------------------------------------------------------------
    // Sub-menus
    // -------------------------------------------------------------------------

    /**
     * Set a parent's open state.
     *
     * @param {HTMLButtonElement} button
     * @param {boolean} open
     */
    const setSubmenu = (button, open) => {
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        button.parentElement.classList.toggle('is-open', open);
    };

    const syncHeaderState = () => {
        header.classList.toggle('is-submenu-open', !isMobile() && parents.some(b => b.getAttribute('aria-expanded') === 'true'));
    };

    const closeSubmenus = () => {
        parents.forEach(b => setSubmenu(b, false));
        syncHeaderState();
    };

    /**
     * Open one parent's sub-menu, closing the others.
     *
     * @param {HTMLButtonElement} button
     */
    const openSubmenu = (button) => {
        parents.forEach(b => setSubmenu(b, b === button));
        syncHeaderState();
    };

    /** Desktop band height: the tallest sub-menu. */
    const measureDropdowns = () => {
        const tallest = Math.max(0, ...parents.map(b => {
            const sub = document.getElementById(b.getAttribute('aria-controls'));
            return sub ? sub.offsetHeight : 0;
        }));
        header.style.setProperty('--header-dropdown-height', `${tallest}px`);
    };

    parents.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            if (button.getAttribute('aria-expanded') === 'true') {
                closeSubmenus();
            } else {
                openSubmenu(button);
            }
        });

        // Desktop: while a drop-down is open, pointing at another parent switches.
        button.parentElement.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse' || isMobile()) return;
            if (header.classList.contains('is-submenu-open') && button.getAttribute('aria-expanded') !== 'true') {
                openSubmenu(button);
            }
        });
    });

    // Desktop: pointing at an item without pages closes the open drop-down.
    panel.querySelectorAll('.header__primary > ul > li:not(.menu-item-has-children)').forEach(li => {
        li.addEventListener('pointerenter', (e) => {
            if (e.pointerType === 'mouse' && !isMobile() && header.classList.contains('is-submenu-open')) {
                closeSubmenus();
            }
        });
    });

    // Desktop: leaving the header (bar + band) closes it after a delay;
    // coming back in time cancels that.
    let leaveTimer = null;
    header.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse' || isMobile() || !header.classList.contains('is-submenu-open')) return;
        clearTimeout(leaveTimer);
        leaveTimer = setTimeout(closeSubmenus, LEAVE_CLOSE_DELAY);
    });
    header.addEventListener('pointerenter', () => clearTimeout(leaveTimer));

    document.addEventListener('click', (e) => {
        if (!isMobile() && header.classList.contains('is-submenu-open') && !header.contains(e.target)) {
            closeSubmenus();
        }
    });

    // -------------------------------------------------------------------------
    // Mobile panel
    // -------------------------------------------------------------------------

    const closePanel = () => {
        body.classList.remove('mobile-menu-open');
        toggle?.setAttribute('aria-expanded', 'false');
        closeSubmenus();
        getLenisInstance()?.start();
    };

    const openPanel = () => {
        if (!isMobile()) return;
        body.classList.add('mobile-menu-open');
        toggle?.setAttribute('aria-expanded', 'true');
        panel.scrollTop = 0;
        getLenisInstance()?.stop();
    };

    /** Close everything without transitions (page changes, crossing md). */
    const closeAllInstant = () => {
        body.classList.add('mobile-menu-no-transition');
        header.classList.add('mobile-menu-no-transition');
        closePanel();
        requestAnimationFrame(() => requestAnimationFrame(() => {
            body.classList.remove('mobile-menu-no-transition');
            header.classList.remove('mobile-menu-no-transition');
        }));
    };

    toggle?.addEventListener('click', (e) => {
        e.preventDefault();
        if (body.classList.contains('mobile-menu-open')) {
            closePanel();
        } else {
            openPanel();
        }
    });

    // Same-page anchors don't reload the page, so close the menu for them.
    panel.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => {
            const samePageAnchor = link.hash
                && link.host === location.host
                && link.pathname === location.pathname;
            if (!samePageAnchor) return;
            if (isMobile()) closePanel();
            else closeSubmenus();
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (body.classList.contains('mobile-menu-open')) {
            closePanel();
            toggle?.focus();
        } else if (header.classList.contains('is-submenu-open')) {
            const open = parents.find(b => b.getAttribute('aria-expanded') === 'true');
            closeSubmenus();
            open?.focus();
        }
    });

    document.addEventListener('barba-leave-start', closeAllInstant);

    let wasMobile = isMobile();
    window.addEventListener('resize', () => {
        const mobile = isMobile();
        if (mobile !== wasMobile) closeAllInstant();
        wasMobile = mobile;
        if (!mobile) measureDropdowns();
    });

    if (!isMobile()) measureDropdowns();
    // Web fonts change the sub-menus' height once they load.
    document.fonts?.ready.then(() => { if (!isMobile()) measureDropdowns(); });
}
